import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  Input,
  Label,
  Select,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GraduationCap, Plus } from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import { listStaff } from "../setup/setup-api.js";
import {
  completeTrainingAssignment,
  createTrainingAssignments,
  createTrainingModule,
  listTrainingAssignments,
  listTrainingModules,
  signOffTrainingCompletion,
  trainingDateLabel,
  trainingDisplayState,
  trainingStateLabel,
  type TrainingAssignment,
  type TrainingDisplayState,
} from "./training-api.js";

function stateTone(
  state: TrainingDisplayState,
): "attention" | "danger" | "neutral" | "positive" {
  return state === "complete"
    ? "positive"
    : state === "expired"
      ? "danger"
      : state === "expires_soon" || state === "awaiting_sign_off"
        ? "attention"
        : "neutral";
}

function ModuleEditor({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const id = useId();
  const create = useMutation({
    mutationFn: (form: FormData) =>
      createTrainingModule({
        content_md: String(form.get("content_md") ?? "").trim() || null,
        renewal_months: form.get("renewal_months")
          ? Number(form.get("renewal_months"))
          : null,
        title: String(form.get("title") ?? "").trim(),
      }),
    onSuccess: onSaved,
  });
  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={create.isPending} form={`${id}-form`} type="submit">
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="New training module"
    >
      <form
        className="space-y-4"
        id={`${id}-form`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          create.mutate(new FormData(event.currentTarget));
        }}
      >
        <div>
          <Label htmlFor={`${id}-name`}>Title</Label>
          <Input className="mt-1.5" id={`${id}-name`} name="title" required />
        </div>
        <div>
          <Label htmlFor={`${id}-content`}>Content</Label>
          <textarea
            className="mt-1.5 min-h-36 w-full rounded-xl border border-stone-300 bg-surface p-3 text-sm"
            id={`${id}-content`}
            name="content_md"
          />
        </div>
        <div>
          <Label htmlFor={`${id}-renewal`}>Renew every (months)</Label>
          <Input
            className="mt-1.5"
            id={`${id}-renewal`}
            max="120"
            min="1"
            name="renewal_months"
            type="number"
          />
        </div>
        {create.error ? (
          <p className="text-sm font-semibold text-red-800" role="alert">
            {displayError(create.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function AssignmentEditor({
  locationId,
  onClose,
  onSaved,
}: {
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const id = useId();
  const shell = useShell();
  const modules = useQuery({
    queryFn: listTrainingModules,
    queryKey: ["training-modules", shell.activeOrganisationId],
  });
  const staff = useQuery({
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const assign = useMutation({
    mutationFn: (form: FormData) => {
      const profileIds = form.getAll("staff_profile_id").map(String);
      if (profileIds.length === 0)
        throw new Error("Choose at least one person.");
      return createTrainingAssignments({
        due_on: String(form.get("due_on") ?? "") || null,
        location_id: locationId,
        staff_profile_ids: profileIds,
        training_module_id: String(form.get("training_module_id")),
      });
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={assign.isPending} form={`${id}-form`} type="submit">
            {assign.isPending ? "Assigning…" : "Assign"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="Assign training"
    >
      <form
        className="space-y-4"
        id={`${id}-form`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          assign.mutate(new FormData(event.currentTarget));
        }}
      >
        <div>
          <Label htmlFor={`${id}-module`}>Module</Label>
          <Select
            className="mt-1.5"
            id={`${id}-module`}
            name="training_module_id"
            required
          >
            <option value="">Choose a module</option>
            {modules.data?.map((module) => (
              <option key={module.id} value={module.id}>
                {module.title}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>People</Label>
          <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-stone-200 bg-surface p-2">
            {staff.data?.map((person) => (
              <label
                className="flex cursor-pointer items-center gap-3 rounded-lg p-2.5 hover:bg-stone-50"
                key={person.id}
              >
                <input
                  className="size-4 accent-emerald-800"
                  name="staff_profile_id"
                  type="checkbox"
                  value={person.id}
                />
                <span>
                  <span className="block text-sm font-bold">
                    {person.display_name}
                  </span>
                  {person.job_title ? (
                    <span className="text-xs text-stone-500">
                      {person.job_title}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor={`${id}-due`}>Due on</Label>
          <Input
            className="mt-1.5"
            id={`${id}-due`}
            name="due_on"
            type="date"
          />
        </div>
        {assign.error ? (
          <p className="text-sm font-semibold text-red-800" role="alert">
            {assign.error instanceof Error && !("status" in assign.error)
              ? assign.error.message
              : displayError(assign.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function AssignmentDetail({
  assignment,
  canManage,
  isOwn,
  onClose,
  onUpdated,
}: {
  assignment: TrainingAssignment;
  canManage: boolean;
  isOwn: boolean;
  onClose: () => void;
  onUpdated: (assignment: TrainingAssignment) => void;
}) {
  const [note, setNote] = useState("");
  const complete = useMutation({
    mutationFn: () => completeTrainingAssignment(assignment.id),
    onSuccess: onUpdated,
  });
  const signOff = useMutation({
    mutationFn: () =>
      signOffTrainingCompletion(assignment.completion!.id, note),
    onSuccess: onUpdated,
  });
  const state = trainingDisplayState(assignment);
  const completion = assignment.completion;
  const canSignOff =
    canManage && completion && !completion.trainer_acknowledged_at;
  return (
    <Dialog onClose={onClose} open size="lg" title={assignment.module.title}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={stateTone(state)}>{trainingStateLabel(state)}</Badge>
        <p className="text-sm text-stone-500">
          {assignment.due_on
            ? `Due ${trainingDateLabel(assignment.due_on)}`
            : "No due date"}
        </p>
      </div>
      {assignment.module.content_md ? (
        <p className="mt-5 text-sm leading-6 whitespace-pre-wrap text-stone-700">
          {assignment.module.content_md}
        </p>
      ) : null}
      <dl className="mt-5 divide-y divide-stone-200 border-y border-stone-200 text-sm">
        <div className="flex items-center justify-between gap-3 py-3">
          <dt className="font-semibold">Completed</dt>
          <dd className="flex items-center gap-3 text-stone-600">
            {completion?.trainee_acknowledged_at
              ? trainingDateLabel(completion.trainee_acknowledged_at)
              : "Not yet"}
            {isOwn && !completion ? (
              <Button
                disabled={complete.isPending}
                onClick={() => complete.mutate()}
                size="sm"
              >
                {complete.isPending ? "Saving…" : "Mark completed"}
              </Button>
            ) : null}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 py-3">
          <dt className="font-semibold">Signed off</dt>
          <dd className="text-stone-600">
            {completion?.trainer_acknowledged_at
              ? trainingDateLabel(completion.trainer_acknowledged_at)
              : "Not yet"}
          </dd>
        </div>
        {completion?.expires_on ? (
          <div className="flex items-center justify-between gap-3 py-3">
            <dt className="font-semibold">Valid until</dt>
            <dd className="text-stone-600">
              {trainingDateLabel(completion.expires_on)}
            </dd>
          </div>
        ) : null}
      </dl>
      {canSignOff ? (
        <div className="mt-5">
          <Label htmlFor="competency-note">Note</Label>
          <textarea
            className="mt-1.5 min-h-20 w-full rounded-xl border border-stone-300 bg-surface p-3 text-sm"
            id="competency-note"
            onChange={(event) => setNote(event.target.value)}
            value={note}
          />
          <div className="mt-3 text-right">
            <Button
              disabled={signOff.isPending}
              onClick={() => signOff.mutate()}
            >
              {signOff.isPending ? "Signing off…" : "Sign off"}
            </Button>
          </div>
        </div>
      ) : null}
      {complete.error || signOff.error ? (
        <p className="mt-4 text-sm font-semibold text-red-800" role="alert">
          {displayError(complete.error ?? signOff.error)}
        </p>
      ) : null}
    </Dialog>
  );
}

export function TrainingWorkspacePage({ tabs }: { tabs?: ReactNode }) {
  const shell = useShell();
  const queryClient = useQueryClient();
  const locationId = shell.activeLocation?.id;
  const canManage = shell.activeRole !== "staff";
  const [assigning, setAssigning] = useState(false);
  const [creatingModule, setCreatingModule] = useState(false);
  const [selected, setSelected] = useState<TrainingAssignment | null>(null);
  const modules = useQuery({
    enabled: Boolean(locationId),
    queryFn: listTrainingModules,
    queryKey: ["training-modules", shell.activeOrganisationId],
  });
  const assignments = useQuery({
    enabled: Boolean(locationId),
    queryFn: listTrainingAssignments,
    queryKey: ["training-assignments", shell.activeOrganisationId],
  });
  const staff = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listStaff(locationId!),
    queryKey: ["staff", locationId],
  });
  const visibleAssignments =
    assignments.data?.filter((item) => item.location_id === locationId) ?? [];

  return (
    <>
      {tabs || (canManage && locationId) ? (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">{tabs}</div>
          {canManage && locationId ? (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setCreatingModule(true)} variant="outline">
                <Plus aria-hidden="true" className="size-4" /> New module
              </Button>
              <Button onClick={() => setAssigning(true)}>
                <GraduationCap aria-hidden="true" className="size-4" /> Assign
                training
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {!locationId ? (
        <EmptyState title="Choose a location" />
      ) : assignments.isPending || modules.isPending ? (
        <LoadingState label="Loading training" />
      ) : assignments.error || modules.error ? (
        <ErrorState
          message={displayError(assignments.error ?? modules.error)}
          onAction={() => {
            void assignments.refetch();
            void modules.refetch();
          }}
          title="Training isn’t available"
        />
      ) : canManage ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="sticky left-0 z-[1] bg-stone-50 px-4 py-3 text-xs font-black text-stone-500">
                      Module
                    </th>
                    {staff.data?.map((person) => (
                      <th
                        className="min-w-36 px-3 py-3 text-xs font-black text-stone-500"
                        key={person.id}
                      >
                        {person.display_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.data?.map((module) => (
                    <tr
                      className="border-b border-stone-100 last:border-0"
                      key={module.id}
                    >
                      <th className="sticky left-0 z-[1] max-w-56 bg-surface px-4 py-4">
                        <p className="text-sm font-black">{module.title}</p>
                      </th>
                      {staff.data?.map((person) => {
                        const assignment = visibleAssignments.find(
                          (item) =>
                            item.training_module_id === module.id &&
                            item.staff_profile_id === person.id,
                        );
                        const state = assignment
                          ? trainingDisplayState(assignment)
                          : null;
                        return (
                          <td className="px-3 py-4" key={person.id}>
                            {assignment && state ? (
                              <button
                                onClick={() => setSelected(assignment)}
                                type="button"
                              >
                                <Badge tone={stateTone(state)}>
                                  {trainingStateLabel(state)}
                                </Badge>
                              </button>
                            ) : (
                              <span className="text-xs text-stone-400">
                                <span aria-hidden="true">—</span>
                                <span className="sr-only">Not assigned</span>
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {modules.data?.length === 0 ? (
              <div className="p-5">
                <EmptyState calm title="Add your first module" />
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : visibleAssignments.length === 0 ? (
        <EmptyState calm title="No training assigned" />
      ) : (
        <Card>
          <ul className="divide-y divide-stone-200">
            {visibleAssignments.map((assignment) => {
              const state = trainingDisplayState(assignment);
              return (
                <li key={assignment.id}>
                  <button
                    className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-emerald-800"
                    onClick={() => setSelected(assignment)}
                    type="button"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold">
                        {assignment.module.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-stone-500">
                        {assignment.due_on
                          ? `Due ${trainingDateLabel(assignment.due_on)}`
                          : "No due date"}
                      </span>
                    </span>
                    <Badge tone={stateTone(state)}>
                      {trainingStateLabel(state)}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
      {assigning && locationId ? (
        <AssignmentEditor
          locationId={locationId}
          onClose={() => setAssigning(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ["training-assignments"],
            });
            setAssigning(false);
          }}
        />
      ) : null}
      {creatingModule ? (
        <ModuleEditor
          onClose={() => setCreatingModule(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ["training-modules"],
            });
            setCreatingModule(false);
          }}
        />
      ) : null}
      {selected ? (
        <AssignmentDetail
          assignment={selected}
          canManage={canManage}
          isOwn={shell.ownStaffProfileIds.includes(selected.staff_profile_id)}
          onClose={() => setSelected(null)}
          onUpdated={async (updated) => {
            setSelected(updated);
            await queryClient.invalidateQueries({
              queryKey: ["training-assignments"],
            });
          }}
        />
      ) : null}
    </>
  );
}
